import { cn } from "@/shared/lib/utils";

import type { AcqCardProduct, AcqOpportunityCard } from "../../api/acqService";

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * 一张卡上的产品 chip 们(契约 §19.3)—— 商机卡抬头与线索库「产品」列**共用这一份**
 * ═══════════════════════════════════════════════════════════════════════════
 * 根因(契约 §19 开头):一张商机只有一个 `matched_product_code`,精判在同一条信号上
 * 总选更贴的那个 ⇒ 「销帮AI智脑」「阳光 400 电话」两个页签**恒 0 商机**。批次八让精判
 * 给出 `products`(主产品在前,≤3),这里把它们全部摆出来。
 *
 * ★ 主产品**实心**(有底色)、次产品**描边**(只有一圈线):两者都是"这家能卖的产品",
 *   但排序、归属、我的生意那一列读的仍然是主产品 —— 面上分不出主次的话,
 *   用户会以为这张卡同时算进了三个产品的 KPI。
 * ★ 一个产品时长相与上一版**逐字一样**(实心那一枚):这一批不该让 33 个只有一条
 *   产品线的租户看到任何变化。
 * ═══════════════════════════════════════════════════════════════════════════
 */

/**
 * 卡 → 要摆的产品列表。**消费侧兜底都走这一个函数**(卡、表格、抽屉三处)。
 *
 * ★★ `products` 缺席 / 空数组 ⇒ 退回 `matchedProductCode + matchedProductName` 拼的**单枚**
 *   —— 老后端(批次八那辆 api 车还没上线)发的就是那一对。少了这条兜底,
 *   演示机上今天还能看见的产品 chip 明天会整片消失,而页面一个错都不报。
 * ★ 按 `code` 去重并**保留后端给的顺序**(主产品在前是后端的契约):
 *   前端照名字或按码重排的后果是同一张卡在两页上主次不一样,而两页都不报错。
 * ★ 空 code 的条目丢掉:它点下去 = 不筛 = 全部卡(与 `productCode=""` 逐字同形)。
 */
export function cardProducts(card: AcqOpportunityCard): AcqCardProduct[] {
  const raw = card.products ?? [];
  const list = raw.length > 0 ? raw : fallbackProducts(card);
  const seen = new Set<string>();
  return list.filter((p) => {
    const code = p?.code?.trim();
    if (!code || seen.has(code)) return false;
    seen.add(code);
    return true;
  });
}

function fallbackProducts(card: AcqOpportunityCard): AcqCardProduct[] {
  if (!card.matchedProductCode) return [];
  return [{ code: card.matchedProductCode, name: card.matchedProductName ?? null }];
}

export interface ProductChipsProps {
  card: AcqOpportunityCard;
  /**
   * 点某枚 chip 会发生什么(契约 §19.3:「点任一 chip 切到该产品页签」)。
   *
   * ★ **缺席 ⇒ 全是不能点的标签**(线索库抽屉、样式桩里都是这样):
   *   一个长得像按钮却点不动的 chip 比一块标签坏。
   * ★ 送出去的是 `code` 不是名字 —— 页签认的是码,而产品改名之后名字对不上,
   *   那时面上没有任何一处会报错。
   */
  onPick?: (productCode: string) => void;
  className?: string;
}

export function ProductChips({ card, onPick, className }: ProductChipsProps) {
  const products = cardProducts(card);
  if (products.length === 0) return null;
  return (
    <>
      {products.map((p, i) => (
        <ProductChip
          key={p.code}
          product={p}
          primary={i === 0}
          onPick={onPick}
          className={className}
        />
      ))}
    </>
  );
}

function ProductChip({
  product,
  primary,
  onPick,
  className,
}: {
  product: AcqCardProduct;
  primary: boolean;
  onPick?: (productCode: string) => void;
  className?: string;
}) {
  /**
   * 下架 = 名字没了(契约 §19.3「下架的名字为 null 时显示 code 灰」)。
   * ★★ 下架的那一枚**点不动**:产品页签那一排是照在售产品画的,里头没有它那一格 ——
   *   点下去列表变了而整排页签一格都不亮,用户看不出自己现在在看什么。
   *   灰着、带 title 说明"这个产品已下架",比一个会把页面带到无主状态的按钮诚实。
   */
  const retired = !product.name;
  const label = product.name || product.code;
  const base = cn(
    "max-w-[10rem] truncate rounded-full px-1.5 py-px font-semibold",
    retired
      ? // ★ 真渲染(深色皮肤)抓到的:只给 `bg-muted` 的那一枚几乎与卡底同色,
        //   读起来像一行掉出来的散字而不是一枚 chip。虚线边让它仍然是"一枚",
        //   而虚线本身就在说"这个不一样"。
        "border border-dashed border-muted-foreground/50 bg-muted/40 text-muted-foreground"
      : primary
        ? "bg-primary/10 text-primary"
        : "border border-primary/40 text-primary/80",
    className
  );

  if (retired || !onPick) {
    return (
      <span
        className={base}
        title={retired ? `${product.code}（这个产品已下架）` : label}
        data-product-code={product.code}
      >
        {label}
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={(e) => {
        // ★ 线索库里这枚 chip 坐在**整行可点**的 `<tr>` 里:不掐掉冒泡的话,
        //   点产品会顺带把抽屉也打开 —— 用户要的是换页签,不是看这一条。
        e.stopPropagation();
        onPick(product.code);
      }}
      title={`只看「${label}」的商机`}
      className={cn(base, "transition-colors hover:bg-primary/20")}
      data-product-code={product.code}
    >
      {label}
    </button>
  );
}
